import { supabase } from './supabase';

export type ActivityActionType =
  | 'QUOTATION_CREATED'
  | 'QUOTATION_UPDATED'
  | 'PDF_GENERATED'
  | 'EXCEL_GENERATED'
  | 'EXCEL_UPLOADED'
  | 'REPORT_EXPORTED'
  | 'USER_LOGIN'
  | 'SESSION_HEARTBEAT';

export interface ActivityLogRecord {
  id?: number | string;
  user_id?: string;
  email: string;
  user_name?: string;
  role?: string;
  action_type: ActivityActionType;
  resource_id?: string;
  details?: string;
  ip_address?: string;
  device_type?: string;
  browser?: string;
  created_at: string;
}

export interface UserMetricSummary {
  email: string;
  name: string;
  role: string;
  quotesCreated: number;
  pdfsGenerated: number;
  excelsGenerated: number;
  excelsUploaded: number;
  totalActions: number;
  lastActiveAt: string;
  firstActiveAt: string;
  lastAction: string;
  activeMinutes: number;
  activeDaysCount: number;
  sessionCount: number;
}

export interface DateWiseMetricSummary {
  date: string; // 'YYYY-MM-DD'
  quotesCreated: number;
  pdfsGenerated: number;
  excelsGenerated: number;
  excelsUploaded: number;
  activeUsersCount: number;
  totalActions: number;
  activeUsers: string[];
}

export interface ActivityDashboardMetrics {
  totalQuotes: number;
  totalPdfs: number;
  totalExcels: number;
  totalUploads: number;
  totalActions: number;
  totalUsageMinutes: number;
  todayQuotes: number;
  todayPdfs: number;
  todayExcels: number;
  todayUploads: number;
  userMetrics: UserMetricSummary[];
  dateMetrics: DateWiseMetricSummary[];
  recentLogs: ActivityLogRecord[];
}

const LOCAL_STORAGE_KEY = 'ski_activity_logs_v1';
let cachedIp = '';

// Helper: Format minutes into human readable "2h 45m" or "45m"
export function formatUsageDuration(totalMinutes: number): string {
  if (!totalMinutes || totalMinutes <= 0) return '5m';
  const hours = Math.floor(totalMinutes / 60);
  const mins = Math.round(totalMinutes % 60);
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
}

// Helper: Device detection
function detectDevice(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'Mobile Phone';
  if (/ipad|tablet/i.test(ua)) return 'Tablet';
  if (/macintosh|mac os x/i.test(ua)) return 'Mac Desktop';
  if (/windows/i.test(ua)) return 'Windows PC';
  if (/linux/i.test(ua)) return 'Linux PC';
  return 'Desktop';
}

// Helper: Browser detection
function detectBrowser(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome/')) return 'Chrome';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
  return 'Browser';
}

// Helper: Non-blocking IP resolution with memory cache
async function getClientIp(): Promise<string> {
  if (cachedIp) return cachedIp;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);
    const res = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
    clearTimeout(timer);
    if (res.ok) {
      const data = await res.json();
      cachedIp = data.ip || 'Client Direct';
      return cachedIp;
    }
  } catch {
    // Non-blocking fallback
  }
  return 'Local / Intranet';
}

// Save to LocalStorage with capacity cap
function saveToLocalStorage(record: ActivityLogRecord) {
  try {
    if (typeof window === 'undefined') return;
    const existingRaw = localStorage.getItem(LOCAL_STORAGE_KEY);
    const existing: ActivityLogRecord[] = existingRaw ? JSON.parse(existingRaw) : [];
    existing.unshift(record);
    // Keep last 500 entries locally
    const trimmed = existing.slice(0, 500);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(trimmed));
  } catch (err) {
    console.warn('Could not save to localStorage activity log:', err);
  }
}

export function getLocalStorageLogs(): ActivityLogRecord[] {
  try {
    if (typeof window === 'undefined') return [];
    const existingRaw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return existingRaw ? JSON.parse(existingRaw) : [];
  } catch {
    return [];
  }
}

/**
 * Log user activity across the application.
 * Persists to Supabase `activity_audit_logs` and local fallback.
 */
export async function logUserActivity(params: {
  action_type: ActivityActionType;
  resource_id?: string;
  details?: string;
  user?: {
    id?: string;
    email?: string;
    name?: string;
    role?: string;
  };
}): Promise<void> {
  try {
    let email = params.user?.email;
    let name = params.user?.name;
    let role = params.user?.role;
    let userId = params.user?.id;

    // If user info not passed, attempt resolution from Supabase Auth or active session
    if (!email && typeof window !== 'undefined') {
      try {
        const { data } = await supabase.auth.getUser();
        if (data?.user) {
          userId = data.user.id;
          email = data.user.email;
          name = data.user.user_metadata?.name || email?.split('@')[0];
          role = data.user.user_metadata?.role || 'staff';
        }
      } catch {
        // Continue with fallback
      }
    }

    const ip = await getClientIp();
    const timestamp = new Date().toISOString();

    const record: ActivityLogRecord = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      user_id: userId,
      email: email || 'anonymous@portal.ski',
      user_name: name || (email ? email.split('@')[0] : 'Authorized User'),
      role: role || 'user',
      action_type: params.action_type,
      resource_id: params.resource_id,
      details: params.details,
      ip_address: ip,
      device_type: detectDevice(),
      browser: detectBrowser(),
      created_at: timestamp,
    };

    // 1. Always record in local storage immediately
    saveToLocalStorage(record);

    // 2. Insert into Supabase table (non-blocking)
    const { error } = await supabase.from('activity_audit_logs').insert([
      {
        user_id: record.user_id,
        email: record.email,
        user_name: record.user_name,
        role: record.role,
        action_type: record.action_type,
        resource_id: record.resource_id,
        details: record.details,
        ip_address: record.ip_address,
        device_type: record.device_type,
        browser: record.browser,
        created_at: record.created_at,
      },
    ]);

    if (error) {
      console.warn('⚠️ [ActivityLogger] Supabase insert warning (saved locally):', error.message);
    }
  } catch (err) {
    console.warn('⚠️ [ActivityLogger] Error logging user activity (non-fatal):', err);
  }
}

/**
 * Start periodic session heartbeat tracking for active staff.
 * Logs heartbeat every 5 minutes when user is interacting.
 */
let heartbeatInterval: any = null;
export function initSessionUsageTracker(user: { email: string; name?: string; role?: string }) {
  if (typeof window === 'undefined') return;
  if (heartbeatInterval) clearInterval(heartbeatInterval);

  // Initial login activity
  logUserActivity({
    action_type: 'USER_LOGIN',
    resource_id: 'SESSION_START',
    details: 'User authenticated and started session',
    user,
  });

  // Track heartbeat every 5 minutes
  heartbeatInterval = setInterval(() => {
    if (document.visibilityState === 'visible') {
      logUserActivity({
        action_type: 'SESSION_HEARTBEAT',
        resource_id: 'HEARTBEAT',
        details: 'Active operational session heartbeat',
        user,
      });
    }
  }, 5 * 60 * 1000);
}

/**
 * Calculate active usage duration from user event timestamps
 * Groups activities into clusters with gap threshold <= 30 minutes.
 */
function calculateUserActiveTime(timestamps: number[]): {
  activeMinutes: number;
  sessionCount: number;
  activeDaysCount: number;
} {
  if (!timestamps || timestamps.length === 0) {
    return { activeMinutes: 0, sessionCount: 0, activeDaysCount: 0 };
  }

  const sorted = [...timestamps].sort((a, b) => a - b);
  const GAP_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
  const DEFAULT_EVENT_SPAN_MS = 5 * 60 * 1000; // 5 minutes buffer per event

  let totalMs = 0;
  let sessionCount = 1;
  let clusterStart = sorted[0];
  let clusterEnd = sorted[0];

  const uniqueDays = new Set<string>();
  uniqueDays.add(new Date(sorted[0]).toISOString().split('T')[0]);

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    uniqueDays.add(new Date(current).toISOString().split('T')[0]);

    if (current - clusterEnd <= GAP_THRESHOLD_MS) {
      clusterEnd = current;
    } else {
      // Finalize previous cluster
      const duration = Math.max(clusterEnd - clusterStart, DEFAULT_EVENT_SPAN_MS);
      totalMs += duration;
      sessionCount++;
      clusterStart = current;
      clusterEnd = current;
    }
  }

  // Finalize last cluster
  const lastDuration = Math.max(clusterEnd - clusterStart, DEFAULT_EVENT_SPAN_MS);
  totalMs += lastDuration;

  return {
    activeMinutes: Math.round(totalMs / (60 * 1000)),
    sessionCount,
    activeDaysCount: uniqueDays.size,
  };
}

/**
 * Fetch and aggregate complete metrics for the hidden super admin portal:
 * - Direct queries to `quotations` table to include historical creation counts
 * - Queries to `activity_audit_logs` + local fallback
 * - User-wise usage time & action breakdown
 * - Day-by-day date timeline breakdown
 */
export async function getActivityDashboardMetrics(): Promise<ActivityDashboardMetrics> {
  const localLogs = getLocalStorageLogs();
  let dbLogs: ActivityLogRecord[] = [];
  let quotationsList: Array<{
    id: number;
    quotation_number: string;
    created_by?: string;
    rep_id?: number;
    created_at?: string;
  }> = [];
  let salesReps: Array<{ id: number; name: string; code: string }> = [];
  let userProfiles: Array<{ id: string; email: string; name: string; role: string }> = [];

  // Fetch Supabase data in parallel
  try {
    const [auditRes, quotesRes, repsRes, profilesRes] = await Promise.allSettled([
      supabase.from('activity_audit_logs').select('*').order('created_at', { ascending: false }).limit(2000),
      supabase.from('quotations').select('id, quotation_number, created_by, rep_id, created_at'),
      supabase.from('sales_reps').select('id, name, code'),
      supabase.from('user_profiles').select('id, email, name, role'),
    ]);

    if (auditRes.status === 'fulfilled' && auditRes.value.data) {
      dbLogs = auditRes.value.data as ActivityLogRecord[];
    }
    if (quotesRes.status === 'fulfilled' && quotesRes.value.data) {
      quotationsList = quotesRes.value.data;
    }
    if (repsRes.status === 'fulfilled' && repsRes.value.data) {
      salesReps = repsRes.value.data;
    }
    if (profilesRes.status === 'fulfilled' && profilesRes.value.data) {
      userProfiles = profilesRes.value.data;
    }
  } catch (e) {
    console.warn('Error fetching Supabase audit metrics:', e);
  }

  // Merge Supabase logs and local storage logs
  const mergedMap = new Map<string, ActivityLogRecord>();
  dbLogs.forEach((log) => {
    const key = `${log.email}_${log.action_type}_${log.resource_id || ''}_${log.created_at}`;
    mergedMap.set(key, log);
  });
  localLogs.forEach((log) => {
    const key = `${log.email}_${log.action_type}_${log.resource_id || ''}_${log.created_at}`;
    if (!mergedMap.has(key)) {
      mergedMap.set(key, log);
    }
  });

  const allLogs = Array.from(mergedMap.values()).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const todayStr = new Date().toISOString().split('T')[0];

  let totalQuotes = 0;
  let totalPdfs = 0;
  let totalExcels = 0;
  let totalUploads = 0;
  let todayQuotes = 0;
  let todayPdfs = 0;
  let todayExcels = 0;
  let todayUploads = 0;

  const userMap = new Map<string, UserMetricSummary>();
  const userTimestampsMap = new Map<string, number[]>();
  const dateMap = new Map<string, DateWiseMetricSummary>();

  const getOrCreateDate = (dateStr: string) => {
    if (!dateMap.has(dateStr)) {
      dateMap.set(dateStr, {
        date: dateStr,
        quotesCreated: 0,
        pdfsGenerated: 0,
        excelsGenerated: 0,
        excelsUploaded: 0,
        activeUsersCount: 0,
        totalActions: 0,
        activeUsers: [],
      });
    }
    return dateMap.get(dateStr)!;
  };

  const getOrCreateUser = (email: string, defaultName?: string, defaultRole?: string) => {
    const cleanEmail = email.toLowerCase().trim();
    if (!userMap.has(cleanEmail)) {
      userMap.set(cleanEmail, {
        email: cleanEmail,
        name: defaultName || cleanEmail.split('@')[0],
        role: defaultRole || 'sales_rep',
        quotesCreated: 0,
        pdfsGenerated: 0,
        excelsGenerated: 0,
        excelsUploaded: 0,
        totalActions: 0,
        lastActiveAt: '',
        firstActiveAt: '',
        lastAction: 'None',
        activeMinutes: 0,
        activeDaysCount: 0,
        sessionCount: 0,
      });
      userTimestampsMap.set(cleanEmail, []);
    }
    return userMap.get(cleanEmail)!;
  };

  // Pre-fill registered profiles
  userProfiles.forEach((p) => {
    if (p.email) {
      getOrCreateUser(p.email, p.name, p.role);
    }
  });

  // Calculate from all logged events
  allLogs.forEach((log) => {
    const u = getOrCreateUser(log.email, log.user_name, log.role);
    u.totalActions++;

    const logTimeMs = new Date(log.created_at).getTime();
    if (!isNaN(logTimeMs)) {
      userTimestampsMap.get(u.email)?.push(logTimeMs);
    }

    if (!u.firstActiveAt || new Date(log.created_at) < new Date(u.firstActiveAt)) {
      u.firstActiveAt = log.created_at;
    }
    if (!u.lastActiveAt || new Date(log.created_at) > new Date(u.lastActiveAt)) {
      u.lastActiveAt = log.created_at;
      u.lastAction = log.action_type;
    }

    const logDate = log.created_at ? log.created_at.split('T')[0] : todayStr;
    const isToday = logDate === todayStr;
    const dayEntry = getOrCreateDate(logDate);
    dayEntry.totalActions++;
    if (!dayEntry.activeUsers.includes(u.email)) {
      dayEntry.activeUsers.push(u.email);
    }

    switch (log.action_type) {
      case 'QUOTATION_CREATED':
        totalQuotes++;
        u.quotesCreated++;
        dayEntry.quotesCreated++;
        if (isToday) todayQuotes++;
        break;
      case 'PDF_GENERATED':
        totalPdfs++;
        u.pdfsGenerated++;
        dayEntry.pdfsGenerated++;
        if (isToday) todayPdfs++;
        break;
      case 'EXCEL_GENERATED':
      case 'REPORT_EXPORTED':
        totalExcels++;
        u.excelsGenerated++;
        dayEntry.excelsGenerated++;
        if (isToday) todayExcels++;
        break;
      case 'EXCEL_UPLOADED':
        totalUploads++;
        u.excelsUploaded++;
        dayEntry.excelsUploaded++;
        if (isToday) todayUploads++;
        break;
      default:
        break;
    }
  });

  // Ensure all quotations in the database table are accounted for in the quotation count & dates
  const repIdToRep = new Map<number, { name: string; code: string }>();
  salesReps.forEach((r) => repIdToRep.set(r.id, r));

  const profileIdToProfile = new Map<string, { email: string; name: string; role: string }>();
  userProfiles.forEach((p) => profileIdToProfile.set(p.id, p));

  if (quotationsList.length > totalQuotes) {
    totalQuotes = quotationsList.length;

    quotationsList.forEach((q) => {
      let targetEmail = '';
      let targetName = '';
      if (q.created_by && profileIdToProfile.has(q.created_by)) {
        const p = profileIdToProfile.get(q.created_by)!;
        targetEmail = p.email;
        targetName = p.name;
      } else if (q.rep_id && repIdToRep.has(q.rep_id)) {
        const r = repIdToRep.get(q.rep_id)!;
        targetEmail = `${r.code.toLowerCase()}@skindustries.in`;
        targetName = r.name;
      }

      const qDate = q.created_at ? q.created_at.split('T')[0] : todayStr;
      const dayEntry = getOrCreateDate(qDate);

      if (targetEmail) {
        const u = getOrCreateUser(targetEmail, targetName);
        if (u.quotesCreated === 0) {
          u.quotesCreated++;
          u.totalActions++;
          if (q.created_at) {
            const timeMs = new Date(q.created_at).getTime();
            if (!isNaN(timeMs)) userTimestampsMap.get(u.email)?.push(timeMs);
          }
        }
      }

      if (dayEntry.quotesCreated === 0) {
        dayEntry.quotesCreated++;
        dayEntry.totalActions++;
      }
    });

    const quotesTodayInDb = quotationsList.filter((q) => q.created_at?.startsWith(todayStr)).length;
    if (quotesTodayInDb > todayQuotes) {
      todayQuotes = quotesTodayInDb;
    }
  }

  // Compute active usage duration for each user
  let grandTotalUsageMinutes = 0;
  userMap.forEach((u) => {
    const tms = userTimestampsMap.get(u.email) || [];
    const stats = calculateUserActiveTime(tms);
    u.activeMinutes = stats.activeMinutes;
    u.sessionCount = stats.sessionCount;
    u.activeDaysCount = stats.activeDaysCount;
    grandTotalUsageMinutes += stats.activeMinutes;
  });

  // Calculate active users per date
  dateMap.forEach((d) => {
    d.activeUsersCount = d.activeUsers.length;
  });

  const userMetrics = Array.from(userMap.values()).sort(
    (a, b) => b.totalActions - a.totalActions || b.activeMinutes - a.activeMinutes
  );

  const dateMetrics = Array.from(dateMap.values()).sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return {
    totalQuotes,
    totalPdfs,
    totalExcels,
    totalUploads,
    totalActions: totalQuotes + totalPdfs + totalExcels + totalUploads + allLogs.length,
    totalUsageMinutes: grandTotalUsageMinutes,
    todayQuotes,
    todayPdfs,
    todayExcels,
    todayUploads,
    userMetrics,
    dateMetrics,
    recentLogs: allLogs,
  };
}
